import { connectError } from "./errors.js";
import { apiError, connectFetch, decodeJsonResponse, parseStored } from "./runtime-utils.js";
import { base64UrlBytes, randomBase64Url } from "./base64.js";
import { withRequestBudget } from "./request-budget.js";
export class ConnectionNotifications {
    context;
    constructor(context) {
        this.context = context;
    }
    async registerNotifications(options) {
        return withRequestBudget(options, this.context.requestTimeoutMs, (budget) => this.registerNotificationsWithinBudget(options, budget.signal));
    }
    async registerNotificationsWithinBudget(options, signal) {
        const token = await this.context.authorizedToken(signal);
        if (!token) {
            throw connectError("not_authorized", "Connect this application before enabling notifications.");
        }
        const application = await this.context.register(signal);
        const declared = application.notifications?.criteria.map((criterion) => criterion.id) ?? [];
        const criteria = [...new Set(options.criteria ?? declared)];
        const undeclared = criteria.find((criterion) => !declared.includes(criterion));
        if (undeclared) {
            throw connectError("notification_criterion_not_declared", `The application manifest does not declare notification criterion ${undeclared}.`);
        }
        if (criteria.length === 0) {
            throw connectError("notifications_not_declared", "This application manifest does not declare any notification criteria.");
        }
        const keyResponse = await connectFetch(`${this.context.serverUrl}/v1/notifications/vapid-public-key`, { signal }, "notifications_unavailable", "Push notifications are unavailable.");
        const keyBody = await decodeJsonResponse(keyResponse, "invalid_operation_response", "Connect returned an invalid notification key response.");
        if (!keyResponse.ok) {
            throw apiError(keyBody, "notifications_unavailable", "Push notifications are unavailable.", keyResponse.status);
        }
        let pushSubscription = await options.serviceWorker.pushManager.getSubscription();
        if (!pushSubscription) {
            pushSubscription = await options.serviceWorker.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: base64UrlBytes(keyBody.public_key)
            });
        }
        const serialized = pushSubscription.toJSON();
        if (!serialized.endpoint || !serialized.keys?.p256dh || !serialized.keys.auth) {
            throw connectError("invalid_push_subscription", "The browser returned an incomplete push subscription.");
        }
        const previous = parseStored(this.context.storage.getItem(this.context.notificationKey()));
        const installationId = options.installationId
            ?? previous?.installationId
            ?? randomBase64Url(24);
        const channelResponse = await connectFetch(`${this.context.serverUrl}/v1/notifications/channels`, {
            method: "POST",
            headers: {
                authorization: `Bearer ${token.accessToken}`,
                "content-type": "application/json"
            },
            body: JSON.stringify({
                installation_id: installationId,
                criteria,
                subscription: {
                    endpoint: serialized.endpoint,
                    expirationTime: serialized.expirationTime ?? null,
                    keys: serialized.keys
                }
            }),
            signal
        }, "notification_registration_failed", "Could not register push notifications.");
        const channelBody = await decodeJsonResponse(channelResponse, "invalid_operation_response", "Connect returned an invalid notification registration response.");
        if (!channelResponse.ok) {
            throw apiError(channelBody, "notification_registration_failed", "Could not register push notifications.", channelResponse.status);
        }
        if (previous?.channelId && previous.channelId !== channelBody.channel_id) {
            void fetch(`${this.context.serverUrl}/v1/notifications/channels/${previous.channelId}`, {
                method: "DELETE",
                headers: { authorization: `Bearer ${token.accessToken}` },
                signal
            }).catch(() => undefined);
        }
        const registration = {
            channelId: channelBody.channel_id,
            installationId,
            criteria
        };
        this.context.storage.setItem(this.context.notificationKey(), JSON.stringify(registration));
        return registration;
    }
    /**
     * Register an iOS or Android installation for Connect-managed FCM.
     *
     * The application manifest selects the Firebase project. Re-register the
     * same installation whenever Firebase refreshes its token.
     */
    async registerNativeNotifications(options) {
        return withRequestBudget(options, this.context.requestTimeoutMs, (budget) => this.registerNativeNotificationsWithinBudget(options, budget.signal));
    }
    async registerNativeNotificationsWithinBudget(options, signal) {
        const token = await this.context.authorizedToken(signal);
        if (!token) {
            throw connectError("not_authorized", "Connect this application before enabling notifications.");
        }
        const application = await this.context.register(signal);
        if (application.notifications?.native_delivery?.mode !== "managed_fcm") {
            throw connectError("managed_fcm_not_declared", "This application does not declare Connect-managed native notifications.");
        }
        const declared = application.notifications.criteria.map((criterion) => criterion.id);
        const criteria = [...new Set(options.criteria ?? declared)];
        const undeclared = criteria.find((criterion) => !declared.includes(criterion));
        if (undeclared) {
            throw connectError("notification_criterion_not_declared", `The application manifest does not declare notification criterion ${undeclared}.`);
        }
        if (criteria.length === 0) {
            throw connectError("notifications_not_declared", "This application manifest does not declare any notification criteria.");
        }
        const storageKey = this.context.notificationKey("fcm");
        const previous = parseStored(this.context.storage.getItem(storageKey));
        const installationId = options.installationId
            ?? previous?.installationId
            ?? randomBase64Url(24);
        const response = await connectFetch(`${this.context.serverUrl}/v1/notifications/channels`, {
            method: "POST",
            headers: {
                authorization: `Bearer ${token.accessToken}`,
                "content-type": "application/json"
            },
            body: JSON.stringify({
                installation_id: installationId,
                criteria,
                transport: "fcm",
                token: options.token
            }),
            signal
        }, "notification_registration_failed", "Could not register native notifications.");
        const body = await decodeJsonResponse(response, "invalid_operation_response", "Connect returned an invalid native notification response.");
        if (!response.ok) {
            throw apiError(body, "notification_registration_failed", "Could not register native notifications.", response.status);
        }
        if (previous?.channelId && previous.channelId !== body.channel_id) {
            void this.deleteNotificationChannel(previous.channelId, token.accessToken, signal)
                .catch(() => undefined);
        }
        const registration = {
            channelId: body.channel_id,
            installationId,
            transport: "fcm",
            criteria
        };
        this.context.storage.setItem(storageKey, JSON.stringify(registration));
        return registration;
    }
    async unregisterNativeNotifications(options = {}) {
        return withRequestBudget(options, this.context.requestTimeoutMs, (budget) => this.unregisterNativeNotificationsWithinBudget(budget.signal));
    }
    async unregisterNativeNotificationsWithinBudget(signal) {
        const storageKey = this.context.notificationKey("fcm");
        const registration = parseStored(this.context.storage.getItem(storageKey));
        const token = await this.context.authorizedToken(signal);
        if (registration?.channelId && !token) {
            throw connectError("not_authorized", "Reconnect this application before disabling native notifications.");
        }
        if (registration?.channelId && token) {
            await this.deleteNotificationChannel(registration.channelId, token.accessToken, signal);
        }
        this.context.storage.removeItem(storageKey);
    }
    async unregisterNotifications(serviceWorker, options = {}) {
        return withRequestBudget(options, this.context.requestTimeoutMs, (budget) => this.unregisterNotificationsWithinBudget(serviceWorker, budget.signal));
    }
    async unregisterNotificationsWithinBudget(serviceWorker, signal) {
        const registration = parseStored(this.context.storage.getItem(this.context.notificationKey()));
        const token = await this.context.authorizedToken(signal);
        if (registration?.channelId && !token) {
            throw connectError("not_authorized", "Reconnect this application before disabling push notifications.");
        }
        if (registration?.channelId && token) {
            const response = await connectFetch(`${this.context.serverUrl}/v1/notifications/channels/${registration.channelId}`, {
                method: "DELETE",
                headers: { authorization: `Bearer ${token.accessToken}` },
                signal
            }, "notification_unregistration_failed", "Could not unregister push notifications.");
            if (!response.ok && response.status !== 404) {
                const body = await decodeJsonResponse(response, "invalid_operation_response", "Connect returned an invalid notification response.");
                throw apiError(body, "notification_unregistration_failed", "Could not unregister push notifications.", response.status);
            }
        }
        this.context.storage.removeItem(this.context.notificationKey());
        const subscription = await serviceWorker?.pushManager.getSubscription();
        await subscription?.unsubscribe();
    }
    async deleteNotificationChannel(channelId, accessToken, signal) {
        const response = await connectFetch(`${this.context.serverUrl}/v1/notifications/channels/${channelId}`, {
            method: "DELETE",
            headers: { authorization: `Bearer ${accessToken}` },
            signal
        }, "notification_unregistration_failed", "Could not unregister push notifications.");
        if (!response.ok && response.status !== 404) {
            const body = await decodeJsonResponse(response, "invalid_operation_response", "Connect returned an invalid notification response.");
            throw apiError(body, "notification_unregistration_failed", "Could not unregister push notifications.", response.status);
        }
    }
}
